import type { ReviewCountryEntry } from '~~/shared/types/models'

export interface Category {
  id: number
  name: string
}

export interface Review {
  id: number
  categories: number[]
  thumbnailUrl: string | null
  title: string
  writerNm: string | null
  countryNm: string | null
  viewCnt: number
  likeCnt: number
  commentCnt: number
  contents: string
  openYn: boolean
  createdAt: string
  images: {
    docFileId: number
    url: string
    sortSeq: number
  }[]
}

export interface ReviewDetail extends Review {
  peacemaker: {
    reviewAgreed: boolean,
    activityDt?: string,
    activityMember?: number,
    activityPlaceGb?: number,
    activityPlaceNm?: string,
    activityPlaceTxt?: string,
    activityDurationNm?: string,
    activityDurationGb?: number,
    materialTypeGb?: number,
    materialTypeNm?: string,
    categoryId?: number,
    categoryName?: string,
  } | null
  prev?: {
    id: number
    title: string
    writerNm: string
    countryNm: string
    thumbnailUrl: string
  } | null
  next: {
    id: number
    title: string
    writerNm: string
    countryNm: string
    thumbnailUrl: string
  } | null
  countryCd: string
  createdUid: number
  isMine: boolean
  autoTransYn: boolean
  isLiked: boolean
  bestComments: number[]
  adminComments: { id: number, comment: string, translated?: string, viewMode?: 'loading' | 'translated' }[]
}

// 목록
export interface Reviews {
  reviews: Review[]
  total: number
}

export interface ReviewDto {
  categories: number[]
  country: string
  name: string
  title: string
  contents: string
  deletedDocFileIds: number[]
  newFiles: { file: File, sortSeq: number }[]
  updatedOrders: { docFileId: number, sortSeq: number }[]
}

export default function () {
  const languageGbSn = useI18n().locale
  const { locale } = useI18n()

  // constants
  const defaultThumbnail = '/images/ico_envelope.svg'
  const categoryStyle = {
    1: 'greeting',
    2: 'thanks',
    3: 'apology',
    4: 'tolerance',
    5: 'yield',
    6: 'esteem',
    7: 'cheerup',
    8: 'consideration',
    9: 'compliment',
    10: 'greeting',
    11: 'greeting',
    12: 'greeting',
  } as { [key: number]: string }

  async function fetchCategories(docGbs?: number[]) {
    const { data: categories } = await useAPI<Category[]>('/hml/v1/review/categories', {
      ...(docGbs ? { query: { docGbs: docGbs?.join(',') } } : {}),
      method: 'GET',
    })

    return categories.value
  }
 
  //국가별 후기 개수 조회
  async function fetchReviewCountries(my: number = 0, category: MaybeRef<number> = 0) {
    const { jwtCookie } = useAuth()

    return await useFetch<ReviewCountryEntry[]>('/api/review/countries', {
      method: 'GET',
      query: { languageGbSn, my, category },
      headers: {
        Authorization: `Bearer ${jwtCookie.value ?? ''}`,
      },
    })
  }

  function _convertReviewDtoToFormData(params: ReviewDto) {
    const formData = new FormData()
    formData.append('languageGbSn', languageGbSn.value)
    formData.append('categories', JSON.stringify(params.categories))
    formData.append('country', params.country)
    formData.append('name', params.name)
    formData.append('title', params.title)
    formData.append('contents', params.contents)

    // 파일 순서 정보
    const fileOrders = params.newFiles.map(f => f.sortSeq)
    formData.append('newFileSortSeqList', JSON.stringify(fileOrders))

    // 파일 추가
    params.newFiles.forEach(({ file }) => {
      formData.append('newFiles', file)
    })

    // 삭제할 파일 ID
    formData.append('deleteDocFileIds', JSON.stringify(params.deletedDocFileIds))

    // 순서 업데이트가 필요한 파일 정보
    formData.append('updatedOrders', JSON.stringify(params.updatedOrders))

    return formData
  }

  async function storeReview(params: ReviewDto) {
    return await useAPI<number>('/hml/v1/review', {
      method: 'POST',
      body: _convertReviewDtoToFormData(params),
    })
  }

  async function updateReview(docId: number, params: ReviewDto) {
    return await useAPI(`/hml/v1/review/${docId}`, {
      method: 'PUT',
      body: _convertReviewDtoToFormData(params),
    })
  }

  function fetchReview(
    docId: MaybeRef<number>,
    options?: {
      withRelated?: MaybeRef<number>
      maskName?: MaybeRef<number>
      countryCd?: MaybeRef<string | undefined>
      category?: MaybeRef<number>
    },
  ) {
    const reviewQueryKey = computed(() => ({
      docId: toValue(docId),
      countryCd: options?.countryCd ? toValue(options.countryCd) : undefined,
      category: options?.category ? toValue(options.category) : 0,
      withRelated: options?.withRelated ? toValue(options.withRelated) : 1,
      maskName: options?.maskName ? toValue(options.maskName) : 1,
    }))

    return useAPI<ReviewDetail | null>(
      () => `/hml/v1/review/${reviewQueryKey.value.docId}`,
      {
        method: 'GET',
        query: computed(() => {
          const { countryCd, category, withRelated, maskName } = reviewQueryKey.value

          return {
            languageGbSn: languageGbSn.value,
            withRelated,
            maskName,
            likeDt: new Date().toISOString().substring(0, 10),
            ...(countryCd ? { countryCd } : {}),
            ...(category > 0 ? { category } : {}),
          }
        }),
        watch: [reviewQueryKey],
      },
    )
  }

  async function updateReviewViewCnt(docId: number): Promise<'already' | 'success'> {
    const today = new Date()
    const tomorrow = new Date()
    tomorrow.setDate(today.getDate() + 1)
    const viewCookie = useCookie<number[]>('HMLReviewViewIds', { expires: tomorrow, default: () => [] })

    if (viewCookie.value.includes(+docId)) {
      return 'already'
    }

    viewCookie.value.push(+docId)
    await useAPI(`/hml/v1/review/${docId}/view`, {
      method: 'PUT',
    })

    return 'success'
  }

  async function toggleReviewLike(docId: number) {
    const { $api } = useNuxtApp()
    const likeDt = new Date().toISOString().substring(0, 10)
    return await $api(`/hml/v1/review/${docId}/toggle-like`, {
      method: 'PUT',
      body: { likeDt },
    })
  }

  async function deleteReview(docId: number) {
    return await useAPI(`/hml/v1/review/${docId}`, {
      method: 'DELETE',
    })
  }

  async function getWs2Token() {
    const { data } = await useAPI<string>('/hml/v1/review/ws2-token', {
      method: 'GET',
    })

    return data.value
  }

  async function fetchReviewCommentToken(id: number) {
    return await useAPI<string>(`/hml/v1/review/${id}/comment-token`, {
      method: 'GET',
      query: {
        languageGbSn: locale.value,
      },
    })
  }

  async function fetchAnniversary2ndCommentToken(id: number) {
    return await useAPI<string>(`/hml/v1/anniversary2nd/post/${id}/comment-token`, {
      method: 'GET',
      query: {
        languageGbSn: locale.value,
      },
    })
  }

  async function fetchNewsCommentToken(id: number) {
    return await useAPI<string>(`/hml/v1/news/${id}/comment-token`, {
      method: 'GET',
      query: {
        languageGbSn: locale.value,
      },
    })
  }

  async function translateAdminComment(commentId: number, targetLang?: string) {
    const { data } = await useAPI<string>(`/hml/v1/admin-comment/${commentId}`, {
      method: 'GET',
      query: {
        languageGbSn: targetLang ?? locale.value,
      },
    })
    return data.value
  }

  return {
    defaultThumbnail,
    categoryStyle,
    fetchCategories,
    fetchReviewCountries,
    storeReview,
    fetchReview,
    updateReview,
    updateReviewViewCnt,
    deleteReview,
    getWs2Token,
    fetchReviewCommentToken,
    fetchAnniversary2ndCommentToken,
    fetchNewsCommentToken,
    toggleReviewLike,
    translateAdminComment,
  }
}
